'use client'
import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types'

import { CellTokenAmountPrice } from '@morpho-org/ui-v6/client'
import { CellSimpleText, Skeleton } from '@morpho-org/ui-v6/server'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import { abbreviateAddress } from '@repo/utils'
import {
  columnFilteringFeature,
  createFilteredRowModel,
  filterFns,
  rowSortingFeature,
  tableFeatures
} from '@tanstack/react-table'

import { ButtonLink } from '@/components/common/ButtonLink'
import TimestampCell from '@/components/common/Cells/TimestampCell'
import { UserCellDashboard, UserCellVaultAware } from '@/components/common/Cells/UserCell'

import type { VaultTransactionsTableRow } from './VaultTransactions.types'

import { COLUMN_IDS } from './config'

const COLUMN_LABELS: Partial<Record<keyof typeof COLUMN_IDS, string>> = {
  [COLUMN_IDS.Timestamp]: 'Date',
  [COLUMN_IDS.TypeText]: 'Type',
  [COLUMN_IDS.AssetsUsd]: 'Amount',
  [COLUMN_IDS.User]: 'User',
  [COLUMN_IDS.TransactionHash]: 'Transaction'
}

interface GetVaultTransactionsColumnsParams {
  /**
   * Which user cell to render, if the surface shows a User column at all:
   * - `vaultAware` — activity feed (vault-aware user page links)
   * - `dashboard` — V2 activity (plain dashboard link)
   * The "your transactions" tables pass nothing and get no User column.
   */
  userColumn?: 'vaultAware' | 'dashboard'
}

/**
 * Shared column factory for every vault transaction table. Column order is
 * Timestamp, Type, Amount, [User], Transaction hash — the same order the four
 * per-surface column files used.
 */
// Keeps enableSorting: false effective; without rowSortingFeature, accessor headers render as sortable.
export const vaultTransactionsTableFeatures = tableFeatures({
  columnFilteringFeature,
  filteredRowModel: createFilteredRowModel(),
  filterFns,
  rowSortingFeature
})
export type VaultTransactionsTableFeatures = typeof vaultTransactionsTableFeatures

export const getVaultTransactionsColumns = ({
  userColumn
}: GetVaultTransactionsColumnsParams = {}): ColumnDefWithSkeleton<
  VaultTransactionsTableFeatures,
  VaultTransactionsTableRow
>[] => {
  const columns: ColumnDefWithSkeleton<
    VaultTransactionsTableFeatures,
    VaultTransactionsTableRow
  >[] = [
    {
      id: COLUMN_IDS.Timestamp,
      accessorKey: COLUMN_IDS.Timestamp,
      header: () => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS.Timestamp}</span>
          </TooltipTrigger>
          <TooltipContent>Timestamp of when the transaction occurred.</TooltipContent>
        </Tooltip>
      ),
      cell: ({ row }) => <TimestampCell timestamp={row.original.timestamp} />,
      enableSorting: true,
      skeletonCell: <Skeleton className="h-[22px] w-[122px] rounded-[20px]" />
    },
    {
      id: COLUMN_IDS.TypeText,
      accessorKey: COLUMN_IDS.TypeText,
      header: () => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS.TypeText}</span>
          </TooltipTrigger>
          <TooltipContent>
            The nature of the transaction, providing more details about the type of transaction.
          </TooltipContent>
        </Tooltip>
      ),
      cell: ({ row }) => <CellSimpleText>{row.original.typeText}</CellSimpleText>,
      enableSorting: false,
      skeletonCell: <Skeleton className="h-[22px] w-[120px] rounded-[20px]" />
    },
    {
      id: COLUMN_IDS.AssetsUsd,
      accessorKey: COLUMN_IDS.AssetsUsd,
      header: () => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS.AssetsUsd}</span>
          </TooltipTrigger>
          <TooltipContent>The amount of tokens involved in the transaction.</TooltipContent>
        </Tooltip>
      ),
      cell: ({ row }) => (
        <CellTokenAmountPrice
          token={row.original.vaultToken}
          amount={row.original.assets}
          value={row.original.assetsUsd ?? undefined}
          withAvatar
        />
      ),
      enableSorting: false,
      skeletonCell: <Skeleton className="h-[22px] w-[150px] rounded-[20px]" />
    }
  ]

  if (userColumn) {
    columns.push({
      id: COLUMN_IDS.User,
      accessorKey: COLUMN_IDS.User,
      header: () => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '14.5px' }}>
              {COLUMN_LABELS.User}
            </span>
          </TooltipTrigger>
          <TooltipContent>User who executed the transaction.</TooltipContent>
        </Tooltip>
      ),
      cell: ({ row }) =>
        userColumn === 'vaultAware' ? (
          <UserCellVaultAware address={row.original.user.address} chainId={row.original.chainId} />
        ) : (
          <UserCellDashboard address={row.original.user.address} />
        ),
      enableSorting: false,
      skeletonCell: (
        <Skeleton className="h-[22px] w-[140px] rounded-[20px]" style={{ marginLeft: '14.5px' }} />
      )
    })
  }

  columns.push({
    id: COLUMN_IDS.TransactionHash,
    accessorKey: COLUMN_IDS.TransactionHash,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '12px' }}>
            {COLUMN_LABELS.TransactionHash}
          </span>
        </TooltipTrigger>
        <TooltipContent>The transaction hash.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <ButtonLink
        href={row.original.urlHash}
        mode="external"
        buttonProps={{
          variant: 'ghost',
          size: 'default',
          label: abbreviateAddress(row.original.hash)
        }}
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[119px] rounded-[20px]" />
  })

  return columns
}
